/**
 * Tiny canvas confetti burst — no extra dependency.
 * Short, colourful, sales-floor friendly (not a 30s firework show).
 */

const OMS_CONFETTI_COLORS = Object.freeze([
  '#7CB342', // lettuce-ish
  '#2F6B3A', // seaweed
  '#F4C430', // mustard pop
  '#E85D4C', // tomato wink
  '#5BA3D9', // sky
  '#FFFFFF',
])

/**
 * @param {{
 *   particleCount?: number,
 *   durationMs?: number,
 *   originX?: number,
 *   originY?: number,
 *   spread?: number,
 *   speed?: number,
 *   sizeScale?: number,
 * }} [options]
 * @returns {() => void} cancel
 */
export function fireCrowsNestConfetti(options = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return () => {}
  }

  const particleCount = options.particleCount ?? 42
  const durationMs = options.durationMs ?? 1400
  const spread = options.spread ?? 1.1
  const speedBoost = options.speed ?? 1
  const sizeScale = options.sizeScale ?? 1
  const canvas = document.createElement('canvas')
  canvas.setAttribute('data-testid', 'crows-nest-confetti')
  canvas.setAttribute('aria-hidden', 'true')
  Object.assign(canvas.style, {
    position: 'fixed',
    inset: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    zIndex: '10050',
  })
  document.body.appendChild(canvas)

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    canvas.remove()
    return () => {}
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  const resize = () => {
    canvas.width = Math.floor(window.innerWidth * dpr)
    canvas.height = Math.floor(window.innerHeight * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }
  resize()

  const originX =
    options.originX != null ? options.originX : window.innerWidth / 2
  const originY =
    options.originY != null
      ? options.originY
      : Math.min(120, window.innerHeight * 0.18)
  const particles = Array.from({ length: particleCount }, () => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * spread
    const speed = (6 + Math.random() * 8) * speedBoost
    return {
      x: originX + (Math.random() - 0.5) * 40,
      y: originY,
      vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 3 * speedBoost,
      vy: Math.sin(angle) * speed - Math.random() * 4 * speedBoost,
      size: (4 + Math.random() * 5) * sizeScale,
      color:
        OMS_CONFETTI_COLORS[
          Math.floor(Math.random() * OMS_CONFETTI_COLORS.length)
        ],
      rotation: Math.random() * Math.PI,
      rotationSpeed: (Math.random() - 0.5) * 0.35,
      wobble: Math.random() * Math.PI * 2,
      gravity: 0.18 + Math.random() * 0.08,
      drag: 0.985,
    }
  })

  const startedAt = performance.now()
  let frameId = 0
  let cleaned = false

  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    cancelAnimationFrame(frameId)
    window.removeEventListener('resize', resize)
    canvas.remove()
  }

  const tick = now => {
    const elapsed = now - startedAt
    if (elapsed > durationMs) {
      cleanup()
      return
    }

    ctx.clearRect(0, 0, window.innerWidth, window.innerHeight)
    const fade = Math.max(0, 1 - elapsed / durationMs)

    particles.forEach(p => {
      p.vy += p.gravity
      p.vx *= p.drag
      p.vy *= p.drag
      p.x += p.vx
      p.y += p.vy
      p.rotation += p.rotationSpeed
      p.wobble += 0.15

      ctx.save()
      ctx.globalAlpha = fade
      ctx.translate(p.x + Math.sin(p.wobble) * 2, p.y)
      ctx.rotate(p.rotation)
      ctx.fillStyle = p.color
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
      ctx.restore()
    })

    frameId = requestAnimationFrame(tick)
  }

  window.addEventListener('resize', resize)
  frameId = requestAnimationFrame(tick)

  return cleanup
}
